import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Footer from "@/components/Footer";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const token = params.get("token");
  const [state, setState] = useState({ status: "pending", tries: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", tries: 0 });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", tries, amount: data.amount, currency: data.currency });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", tries });
          return;
        }
        if (tries >= 12) {
          setState({ status: "timeout", tries });
          return;
        }
        setState({ status: "pending", tries });
        setTimeout(poll, 2000);
      } catch {
        if (tries >= 12) return setState({ status: "error", tries });
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="pt-28 pb-16 min-h-screen">
      <section className="max-w-lg mx-auto px-4 text-center" data-testid="payment-return">
        {state.status === "pending" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-[#E5A93C]" />
            <h1 className="font-serif-display text-3xl mt-6">
              Sto verificando il pagamento…
            </h1>
            <p className="text-[#A39E93] mt-2 text-sm">
              Non chiudere questa pagina, ci vorrà solo un attimo.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
            <h1 className="font-serif-display text-4xl mt-6">
              Acconto <span className="italic text-[#E5A93C]">saldato</span> ✦
            </h1>
            <p className="text-[#F7F4EE]/85 mt-3">
              Hai versato €{" "}
              {Number(state.amount || 0).toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              . Ti abbiamo prenotato la data — NMA ti scrive a breve.
            </p>
            {token && (
              <Link
                to={`/preventivo/${token}`}
                className="av-pill av-pill-gold mt-8 inline-flex"
                data-testid="payment-back-quote"
              >
                Torna al preventivo
              </Link>
            )}
          </>
        )}
        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <>
            <XCircle size={56} className="mx-auto text-red-400" />
            <h1 className="font-serif-display text-3xl mt-6">
              Qualcosa è andato storto
            </h1>
            <p className="text-[#A39E93] mt-2 text-sm">
              Il pagamento non risulta completato. Riprova o contatta NMA.
            </p>
            {token && (
              <Link
                to={`/preventivo/${token}`}
                className="av-pill av-pill-ghost mt-6 inline-flex"
                data-testid="payment-retry"
              >
                Torna al preventivo
              </Link>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
